/// <amd-module name="Artifacts/FeedSettings/redux/Sagas" />

import { filterWellKnownPermissions } from "Artifacts/FeedSettings/helpers/PermissionsHelpers";
import {
  FeedSettingsActions,
  FeedSettingsActionTypes,
  IAddOrEditViewPayload,
  IAddPermissionPayload,
  IDeleteFeedPayload,
  IDeletePermissionPayload,
  IDeleteViewPayload,
  ILoadFeedViewsPayload,
  ILoadViewsPermissionsPayload,
  IMakeViewDefaultPayload,
  IRestoreFeedPayload,
  ISaveGeneralFeedSettingsPayload,
  ISaveUpstreamsPayload,
  ISettingsInitLoadPayload,
} from "Artifacts/FeedSettings/redux/Actions";
import { ViewPermissionMap } from "Artifacts/FeedSettings/redux/Contracts";
import * as Res from "Artifacts/FeedSettings/Resources";
import { FeedRecycleBinService } from "Artifacts/FeedSettings/services/FeedRecycleBinService";
import {
  FeedSettingsService,
  IFeedSettingsService,
} from "Artifacts/FeedSettings/services/FeedSettingsService";
import { all, call, getContext, put, takeEvery } from "Artifacts/Root/Saga";
import { IFeedResolvingService } from "Artifacts/Root/services/FeedResolvingService";
import { IIdentityDataService } from "Artifacts/Root/services/IdentityDataService";
import {
  Feed,
  FeedPermission,
  FeedRole,
  FeedView,
  FeedVisibility,
  UpstreamSource,
} from "Artifacts/Root/web-apis/contracts/Feed";
import { FeedSettings } from "Artifacts/Root/web-apis/contracts/WebPage";
import { Effect, SagaIterator } from "redux-saga";
import { Action, ActionWithPayload } from "redux/Utils/ActionHelper";
import { ServiceEndpoint } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";
import { IGlobalMessagesService } from "TFS/GlobalBanner/GlobalMessagesService.Types";
import { announce } from "VSS/Core/Util/Accessibility";
import * as Utils_String from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { Identity } from "VSS/Platform/Contracts/Identities";

export default function* rootSaga(): SagaIterator {
  yield takeEvery(FeedSettingsActionTypes.InitLoad, initializeData);
  yield takeEvery(FeedSettingsActionTypes.DeleteFeed, deleteFeed);
  yield takeEvery(FeedSettingsActionTypes.PermanentDeleteFeed, permanentDeleteFeed);
  yield takeEvery(FeedSettingsActionTypes.RestoreFeed, restoreFeed);
  yield takeEvery(FeedSettingsActionTypes.SaveGeneralFeedSettings, saveGeneralSettings);
  yield takeEvery(FeedSettingsActionTypes.LoadPermissions, loadPermissions);
  yield takeEvery(FeedSettingsActionTypes.AddPermission, addPermission);
  yield takeEvery(FeedSettingsActionTypes.DeletePermission, deletePermission);
  yield takeEvery(FeedSettingsActionTypes.LoadViewsPermissions, loadViewsPermissions);
  yield takeEvery(FeedSettingsActionTypes.AddOrEditView, addOrEditView);
  yield takeEvery(FeedSettingsActionTypes.MakeViewDefault, updateDefaultView);
  yield takeEvery(FeedSettingsActionTypes.DeleteView, deleteView);
  yield takeEvery(FeedSettingsActionTypes.SaveUpstreamConfiguration, saveUpstreamConfiguration);
  yield takeEvery(FeedSettingsActionTypes.LoadFeeds, loadFeeds);
  yield takeEvery(FeedSettingsActionTypes.LoadFeedViews, loadFeedViews);
  yield takeEvery(FeedSettingsActionTypes.LoadServiceConnections, loadServiceConnections);
}

function* initializeData(
  action: ActionWithPayload<FeedSettingsActionTypes, ISettingsInitLoadPayload>,
): SagaIterator {
  try {
    const { feed } = action.payload;

    yield put(FeedSettingsActions.initLoadStarted());
    const pageContext: IVssPageContext = (yield getContext("pageContext")) as any;
    const feedSettingsService: FeedSettingsService = pageContext.getService("IFeedSettingsService");
    const initialData: FeedSettings = (yield call(
      [feedSettingsService, feedSettingsService.getInitialFeedData],
      pageContext,
      feed,
    )) as any;
    yield put(FeedSettingsActions.initLoadCompleted(initialData));
  } catch (error) {
    // Zero data screen with error text is displayed on failure
    yield put(FeedSettingsActions.initLoadFailed(error.message));
  }
}

function* saveGeneralSettings(
  action: ActionWithPayload<FeedSettingsActionTypes, ISaveGeneralFeedSettingsPayload>,
): SagaIterator {
  const pageContext: IVssPageContext = (yield getContext("pageContext")) as any;
  try {
    announce(Res.FeedSettings_AnnounceSaveStart);
    const { feed, formState, onSuccess } = action.payload;

    const feedSettingsService: FeedSettingsService = pageContext.getService("IFeedSettingsService");

    yield call(
      [feedSettingsService, feedSettingsService.updateFeedDetailsAsync],
      pageContext,
      feed,
      formState,
    );

    announce(Res.FeedSettings_AnnounceSaveSuccess);
    yield call(onSuccess);
    yield put(FeedSettingsActions.saveGeneralFeedSettingsCompleted());
  } catch (error) {
    //announce(Utils_String.format(Res.FeedSettings_AnnounceSaveError, error.message));
    addErrorToast(pageContext, error, 6000);
    yield put(FeedSettingsActions.saveGeneralFeedSettingsFailed(error.message));
  }
}

function* deleteFeed(action: ActionWithPayload<FeedSettingsActionTypes, IDeleteFeedPayload>) {
  const pageContext: IVssPageContext = yield getContext("pageContext");
  try {
    const { feed, onSuccess } = action.payload;
    announce(Utils_String.format(Res.FeedDeletion_AnnounceSaveStart, feed.name));
    const feedRecycleBinService: FeedRecycleBinService = pageContext.getService(
      "IFeedRecycleBinService",
    );
    yield call([feedRecycleBinService, feedRecycleBinService.deleteFeed], pageContext, feed) as any;

    announce(Res.FeedDeletion_AnnounceSaveSuccess);
    yield call(onSuccess);
    yield put(FeedSettingsActions.deleteFeedCompleted());
  } catch (error) {
    //announce(Utils_String.format(Res.FeedDeletion_AnnounceSaveError, error.message));
    // TODO: remove toast once component state is working for generic modal
    addErrorToast(pageContext, error, 4000);
    yield put(FeedSettingsActions.deleteFeedFailed(error.message));
  }
}

function* permanentDeleteFeed(
  action: ActionWithPayload<FeedSettingsActionTypes, IDeleteFeedPayload>,
) {
  const pageContext: IVssPageContext = yield getContext("pageContext");
  try {
    const { feed, onSuccess } = action.payload;
    announce(Utils_String.format(Res.FeedPermanentDeletion_AnnounceSaveStart, feed.name));
    const feedRecycleBinService: FeedRecycleBinService = pageContext.getService(
      "IFeedRecycleBinService",
    );
    yield call(
      [feedRecycleBinService, feedRecycleBinService.permanentDeleteFeed],
      pageContext,
      feed,
    ) as any;

    announce(Res.FeedPermanentDeletion_AnnounceSaveSuccess);
    yield call(onSuccess);
    yield put(FeedSettingsActions.permanentDeleteFeedCompleted());
  } catch (error) {
    //announce(Utils_String.format(Res.FeedPermanentDeletion_AnnounceSaveError, error.message));
    // TODO: remove toast once component state is working for generic modal
    addErrorToast(pageContext, error, 4000);
    yield put(FeedSettingsActions.permanentDeleteFeedFailed(error.message));
  }
}

function* restoreFeed(action: ActionWithPayload<FeedSettingsActionTypes, IRestoreFeedPayload>) {
  const pageContext: IVssPageContext = yield getContext("pageContext");
  try {
    const { feed, onSuccess } = action.payload;
    announce(Utils_String.format(Res.DetailsTab_AnnounceRestoreStart, feed.name));
    const feedRecycleBinService: FeedRecycleBinService = pageContext.getService(
      "IFeedRecycleBinService",
    );
    yield call(
      [feedRecycleBinService, feedRecycleBinService.restoreDeletedFeed],
      pageContext,
      feed,
    ) as any;

    announce(Res.DetailsTab_AnnounceRestoreSuccess);
    yield call(onSuccess);
    yield put(FeedSettingsActions.restoreFeedCompleted());
  } catch (error) {
    //announce(Utils_String.format(Res.DetailsTab_AnnounceRestoreError, error.message));
    addErrorToast(pageContext, error, 4000);
    yield put(FeedSettingsActions.restoreFeedFailed(error.message));
  }
}

function* loadPermissions(
  action: ActionWithPayload<FeedSettingsActionTypes, ISettingsInitLoadPayload>,
): SagaIterator {
  const pageContext: IVssPageContext = (yield getContext("pageContext")) as any;
  try {
    const { feed } = action.payload;
    const feedSettingsService: FeedSettingsService = pageContext.getService("IFeedSettingsService");
    const permissions: FeedPermission[] = (yield call(
      [feedSettingsService, feedSettingsService.getFeedPermissionsAsync],
      pageContext,
      feed,
      undefined,
      true,
      false,
      undefined,
      true,
    )) as any;

    const filteredPermissions = filterWellKnownPermissions(permissions);

    yield put(FeedSettingsActions.loadPermissionsCompleted(filteredPermissions));
  } catch (error) {
    // Zero data screen with error text is displayed on failure
    yield put(FeedSettingsActions.loadPermissionsFailed(error.message));
  }
}

function* addPermission(
  action: ActionWithPayload<FeedSettingsActionTypes, IAddPermissionPayload>,
): SagaIterator {
  try {
    const { feed, permissionToAdd, permissions, onSuccess } = action.payload;
    announce(
      Utils_String.format(
        Res.FeedSettings_Permissions_AnnounceAddStart,
        permissionToAdd.displayName,
        feed.name,
      ),
    );
    const pageContext: IVssPageContext = (yield getContext("pageContext")) as any;
    const feedSettingsService: FeedSettingsService = pageContext.getService("IFeedSettingsService");
    const identityDataService: IIdentityDataService = pageContext.getService(
      "IIdentityDataService",
    );

    // need to get descriptors for recently added user/group because IdentityPicker
    // doesn't return descriptors
    const identities: Identity[] = (yield call(
      [identityDataService, identityDataService.readIdentitiesAsync],
      [permissionToAdd.identityId],
    )) as any;

    identities.forEach((identity: Identity) => {
      permissionToAdd.identityDescriptor = identity.descriptor;
    });

    yield call(
      [feedSettingsService, feedSettingsService.setFeedPermissionsAsync],
      pageContext,
      feed,
      [permissionToAdd],
    );

    const updatedPermissions = [...permissions];
    const existingPermission = updatedPermissions.find(
      (p) => p.identityId === permissionToAdd.identityId,
    );
    if (existingPermission) {
      existingPermission.role = permissionToAdd.role;
    } else {
      updatedPermissions.push(permissionToAdd);
    }

    announce(Res.FeedSettings_Permissions_AnnounceAddSuccess);
    onSuccess();
    // ideally we should just set state.feed.permissions with permissions but server
    // doesn't return all properties only identityDescriptor and role
    yield put(FeedSettingsActions.addPermissionCompleted(updatedPermissions));
  } catch (error) {
    announce(Utils_String.format(Res.FeedSettings_Permissions_AnnounceAddError, error.message));
    // Error is displayed in add permissions panel
    yield put(FeedSettingsActions.addPermissionFailed(error.message));
  }
}

function* deletePermission(
  action: ActionWithPayload<FeedSettingsActionTypes, IDeletePermissionPayload>,
): SagaIterator {
  const pageContext: IVssPageContext = (yield getContext("pageContext")) as any;
  try {
    const { feed, permissionToDelete, permissions, onSuccess } = action.payload;
    announce(
      Utils_String.format(
        Res.FeedSettings_Permissions_AnnounceDeleteStart,
        permissionToDelete.displayName,
        feed.name,
      ),
    );

    const feedSettingsService: FeedSettingsService = pageContext.getService("IFeedSettingsService");

    const deletePermissionPatch: FeedPermission = { ...permissionToDelete };
    deletePermissionPatch.role = FeedRole.None;

    yield call(
      [feedSettingsService, feedSettingsService.setFeedPermissionsAsync],
      pageContext,
      feed,
      [deletePermissionPatch],
    );

    const updatedPermissions = permissions.filter(
      (p) => p.identityId !== deletePermissionPatch.identityId,
    );

    announce(Res.FeedSettings_Permissions_AnnounceDeleteSuccess);
    onSuccess();
    yield put(FeedSettingsActions.deletePermissionCompleted(updatedPermissions));
  } catch (error) {
    //announce(Utils_String.format(Res.FeedSettings_Permissions_AnnounceDeleteError, error.message));
    // TODO: remove toast once component state is working for generic modal
    addErrorToast(pageContext, error, 4000);
    yield put(FeedSettingsActions.deletePermissionFailed(error.message));
  }
}

function* loadViewsPermissions(
  action: ActionWithPayload<FeedSettingsActionTypes, ILoadViewsPermissionsPayload>,
): SagaIterator {
  const pageContext: IVssPageContext = (yield getContext("pageContext")) as any;
  try {
    const { feed, views } = action.payload;

    const feedSettingsService: FeedSettingsService = pageContext.getService("IFeedSettingsService");
    const effects = views.reduce<Record<string, Effect>>((acc, view) => {
      acc[view.id] = call(
        [feedSettingsService, feedSettingsService.getFeedPermissionsAsync],
        pageContext,
        feed,
        view,
        true,
        true,
        undefined,
        true,
      );
      return acc;
    }, {});

    const viewPermissionMap = ((yield all(effects)) as unknown) as ViewPermissionMap;

    yield put(FeedSettingsActions.loadViewsPermissionsCompleted(viewPermissionMap));
  } catch (error) {
    // Zero data screen with error text is displayed on failure
    yield put(FeedSettingsActions.loadViewsPermissionsFailed(error.message));
  }
}

function* addOrEditView(
  action: ActionWithPayload<FeedSettingsActionTypes, IAddOrEditViewPayload>,
): SagaIterator {
  try {
    const {
      feed,
      view,
      isEdit,
      isDefaultEnabled,
      currentViewPermissions,
      feedPermsToSet,
      onSuccess,
    } = action.payload;

    const pageContext: IVssPageContext = (yield getContext("pageContext")) as any;
    isEdit
      ? announce(Utils_String.format(Res.FeedSettings_ViewPanel_AnnounceEditStart, view.name))
      : announce(Utils_String.format(Res.FeedSettings_ViewPanel_AnnounceAddStart, view.name));
    const feedSettingsService: FeedSettingsService = pageContext.getService("IFeedSettingsService");

    // These viewPermissions are for the "Specific People" option only.
    const savedView: FeedView = (yield call(
      [feedSettingsService, feedSettingsService.addUpdateFeedViewAsync],
      pageContext,
      feed,
      view,
      isEdit,
    )) as any;

    // The default view is on the feed object. Update the feed object here if needed.
    let defaultFeedViewId = undefined;
    if (isDefaultEnabled) {
      const returnedFeed: Feed = (yield call(
        [feedSettingsService, feedSettingsService.updateDefaultFeedViewAsync],
        pageContext,
        feed,
        savedView,
      )) as any;
      defaultFeedViewId = returnedFeed.defaultViewId;
    }

    // // We only update permissions for private views -- organization and collection views just save the visiblity state
    let permissions: FeedPermission[] = [];
    if (savedView.visibility === FeedVisibility.Private) {
      permissions = (yield call(
        [feedSettingsService, feedSettingsService.setFeedViewPermissionsAsync],
        pageContext,
        feed,
        savedView,
        currentViewPermissions,
        feedPermsToSet,
      )) as any;
    }

    isEdit
      ? announce(
          Utils_String.format(Res.FeedSettings_ViewPanel_AnnounceEditSuccess, savedView.name),
        )
      : announce(
          Utils_String.format(Res.FeedSettings_ViewPanel_AnnounceAddSuccess, savedView.name),
        );
    onSuccess();
    yield put(
      FeedSettingsActions.addOrEditViewCompleted(savedView, permissions, defaultFeedViewId),
    );
  } catch (error) {
    announce(Utils_String.format(Res.FeedSettings_ViewPanel_Error_Announcement, error.message));
    yield put(FeedSettingsActions.addOrEditViewFailed(error.message));
  }
}

function* updateDefaultView(
  action: ActionWithPayload<FeedSettingsActionTypes, IMakeViewDefaultPayload>,
): SagaIterator {
  const pageContext: IVssPageContext = (yield getContext("pageContext")) as any;
  try {
    const { feed, view } = action.payload;
    announce(
      Utils_String.format(Res.FeedSettings_Views_UpdateDefaultView_AnnounceStart, view.name),
    );
    const feedSettingsService: FeedSettingsService = pageContext.getService("IFeedSettingsService");

    const updatedFeed: Feed = (yield call(
      [feedSettingsService, feedSettingsService.updateDefaultFeedViewAsync],
      pageContext,
      feed,
      view,
    )) as any;

    announce(
      Utils_String.format(Res.FeedSettings_Views_UpdateDefaultView_AnnounceSuccess, view.name),
    );
    yield put(FeedSettingsActions.makeViewDefaultCompleted(updatedFeed.defaultViewId));
  } catch (error) {
    addErrorToast(pageContext, error, 4000);
    yield put(FeedSettingsActions.makeViewDefaultFailed(error.message));
  }
}

function* deleteView(
  action: ActionWithPayload<FeedSettingsActionTypes, IDeleteViewPayload>,
): SagaIterator {
  const pageContext: IVssPageContext = (yield getContext("pageContext")) as any;
  try {
    const { feed, view, onSuccess } = action.payload;
    announce(Utils_String.format(Res.FeedSettings_Views_DeleteView_AnnounceStart, view.name));
    const feedSettingsService: FeedSettingsService = pageContext.getService("IFeedSettingsService");

    (yield call(
      [feedSettingsService, feedSettingsService.deleteFeedViewAsync],
      pageContext,
      feed,
      view,
    )) as any;

    announce(Utils_String.format(Res.FeedSettings_Views_DeleteView_AnnounceSuccess, view.name));
    onSuccess();
    yield put(FeedSettingsActions.deleteViewCompleted(feed, view));
  } catch (error) {
    addErrorToast(pageContext, error, 4000);
    yield put(FeedSettingsActions.deleteViewFailed(error.message));
  }
}

function* saveUpstreamConfiguration(
  action: ActionWithPayload<FeedSettingsActionTypes, ISaveUpstreamsPayload>,
): SagaIterator {
  const pageContext: IVssPageContext = (yield getContext("pageContext")) as any;
  try {
    const { feed, upstreams, onSuccess } = action.payload;
    announce(Res.UpstreamsTab_AnnounceSaveStart);

    const feedSettingsService: FeedSettingsService = pageContext.getService("IFeedSettingsService");
    const updatedUpstreams: UpstreamSource[] = (yield call(
      [feedSettingsService, feedSettingsService.updateUpstreamSources],
      pageContext,
      feed,
      upstreams,
    )) as any;

    announce(Res.UpstreamsTab_AnnounceSaveSuccess);
    onSuccess();
    yield put(FeedSettingsActions.saveUpstreamConfigurationCompleted(updatedUpstreams));
    yield put(FeedSettingsActions.saveUpstreamConfigurationComponentReset());
  } catch (error) {
    addErrorToast(pageContext, error, 6000);
    yield put(FeedSettingsActions.saveUpstreamConfigurationFailed(error.message));
  }
}

function* loadFeeds(_action: Action<FeedSettingsActionTypes>): SagaIterator {
  const pageContext: IVssPageContext = (yield getContext("pageContext")) as any;
  try {
    const feedResolvingService: IFeedResolvingService = pageContext.getService(
      "IFeedResolvingService",
    );
    const feeds: Feed[] = (yield call([
      feedResolvingService,
      feedResolvingService.getFeeds,
    ])) as any;

    yield put(FeedSettingsActions.loadFeedsCompleted(feeds));
  } catch (error) {
    // Error is displayed in the add upstream wizard as a zero data page
    yield put(FeedSettingsActions.loadFeedsFailed(error.message));
  }
}

function* loadFeedViews(
  action: ActionWithPayload<FeedSettingsActionTypes, ILoadFeedViewsPayload>,
): SagaIterator {
  const pageContext: IVssPageContext = (yield getContext("pageContext")) as any;
  try {
    const { feed } = action.payload;

    const feedResolvingService: IFeedResolvingService = pageContext.getService(
      "IFeedResolvingService",
    );
    const views: FeedView[] = (yield call(
      [feedResolvingService, feedResolvingService.getFeedViews],
      feed,
    )) as any;

    yield put(FeedSettingsActions.loadFeedViewsCompleted(views));
  } catch (error) {
    addErrorToast(pageContext, error, 4000);
    yield put(FeedSettingsActions.loadFeedViewsFailed(error.message));
  }
}

function* loadServiceConnections(_action: Action<FeedSettingsActionTypes>): SagaIterator {
  const pageContext: IVssPageContext = (yield getContext("pageContext")) as any;
  try {
    const feedSettingsService: IFeedSettingsService = pageContext.getService(
      "IFeedSettingsService",
    );
    const serviceConnections: ServiceEndpoint[] = (yield call(
      [feedSettingsService, feedSettingsService.getServiceConnectionsForUpstreaming],
      pageContext,
    )) as any;

    yield put(FeedSettingsActions.loadServiceConnectionsCompleted(serviceConnections));
  } catch (error) {
    // Error is displayed in the add service connection upstream panel as a zero data page
    yield put(FeedSettingsActions.loadServiceConnectionsFailed(error.message));
  }
}

function addErrorToast(context: IVssPageContext, error: any, duration: number): void {
  const messageService = context.getService<IGlobalMessagesService>("IGlobalMessagesService");
  messageService.addToast({
    message: error.message,
    duration: duration,
  });
}
